import { ITestProvider } from '../../Features/LoadingBar/loadingBarReducer';
import { IRunTriggerFn } from '../../Features/LoadingBar/LoadingBarActionTypes';
import { Environment } from '../Environment';

export const loadCheqScript = () => {
  const scriptElement: HTMLScriptElement = document.createElement('script');
  scriptElement.setAttribute('type', 'text/javascript');
  scriptElement.setAttribute('async', '');
  scriptElement.setAttribute('data-ch', Environment.CHEQ_CHANNEL);
  scriptElement.setAttribute('class', Environment.CHEQ_CLASS);
  scriptElement.setAttribute('data-jsonp', 'onCheqResponse');
  scriptElement.src = Environment.CHEQ_SCRIPT_URL;

  return new Promise<ITestProvider<HTMLScriptElement>>((resolve) => {
    const timeoutId = setTimeout(
      () =>
        resolve({
          status: 'ERROR',
          reason: 'cheq api failed to load; timeout reached',
        }),
      3000,
    );

    scriptElement.addEventListener(
      'load',
      () => {
        resolve({ status: 'LOADED', instance: scriptElement });
        clearTimeout(timeoutId);
      },
      false,
    );
    scriptElement.onerror = (event) => {
      resolve({
        status: 'ERROR',
        reason:
          typeof event === 'string'
            ? `cheq api failed to load; ${event}`
            : 'cheq api failed to load; error encountered while loading script',
      });
      clearTimeout(timeoutId);
    };

    document.head.appendChild(scriptElement);
  });
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const waitForCheqToBeReady = () =>
  new Promise<ITestProvider<ICheqInstance>>((resolve) => {
    let isTimeoutExceeded = false;
    const timeoutId = setTimeout(() => {
      isTimeoutExceeded = true;
    }, 3000);

    const lookupInstance = async (): Promise<ITestProvider<ICheqInstance>> => {
      while (!isTimeoutExceeded) {
        const instance = {
          request_id: localStorage.getItem('cheq_request_id'),
          vendor: localStorage.getItem('cheq_vendor'),
          threat_type: localStorage.getItem('cheq_threat_type'),
          threat_group: localStorage.getItem('cheq_threat_group'),
          ai_traffic_type: localStorage.getItem('cheq_ai_traffic_type'),
        };
        if (instance.request_id) {
          clearTimeout(timeoutId);
          localStorage.removeItem('cheq_request_id');
          localStorage.removeItem('cheq_vendor');
          localStorage.removeItem('cheq_threat_type');
          localStorage.removeItem('cheq_threat_group');
          localStorage.removeItem('cheq_ai_traffic_type');
          return { status: 'LOADED', instance };
        } else {
          await delay(25);
        }
      }

      return {
        status: 'ERROR',
        reason: 'cheq provider did not load; timeout reached',
      };
    };

    lookupInstance()
      .then(resolve)
      .catch((err) =>
        resolve({
          status: 'ERROR',
          reason:
            err instanceof Error
              ? err.message
              : 'cheq provider did not load; unknown error',
        }),
      );
  });

export const executeCheq =
  (cheq: ICheqInstance): IRunTriggerFn =>
  async () => {
    try {
      const id = cheq.request_id;
      return id
        ? { status: 'LOADED', value: id }
        : { status: 'ERROR', reason: 'cheq execute error; unknown failure' };
    } catch (err) {
      return {
        status: 'ERROR',
        reason:
          err instanceof Error
            ? `cheq execute error; ${err.message}`
            : `cheq execute error; unknown failure`,
      };
    }
  };
